import type { EnvolventeAudio } from '@alter/api-client';
import { NEUTRAL_PARAMS, type RigAdapter, type RigParams } from '@alter/avatar-core';
import { Euler, Quaternion, type Mesh, type Object3D } from 'three';
import { ARKIT_SHAPES, crearPoseBuffer, escribirArkit, escribirMirada, IDX_ARKIT } from './arkit';
import { CapaGestos, GESTOS_REPOSO, type GestosOffsets } from './gestos';
import { CapaHabla, HABLA_SILENCIO, type HablaOffsets, type HablaOpts } from './habla';
import { IdleLayer, IDLE_NEUTRO, type IdleOffsets } from './idle';
import { CapaModulacion } from './modulacion';
import {
  CapaInspiracion,
  CapaPensando,
  INSPIRACION_REPOSO,
  PENSANDO_REPOSO,
  type InspiracionOffsets,
  type PensandoOffsets,
} from './turno';
import {
  CAPAS_VIDA_DEFECTOS,
  clamp,
  GRADOS_A_RAD,
  VIDA_DEFECTOS,
  type CapasVida,
  type Rng,
  type VidaOpts,
} from './vida';

/**
 * Mallas del avatar que llevan blend shapes, cuantas sean. El mismo peso
 * se escribe en todas las que declaren ese nombre en su
 * `morphTargetDictionary`: las pestañas siguen al párpado y los dientes
 * inferiores a la mandíbula. Es plomería, no diseño
 * (`07_BLENDSHAPES_ARKIT.md` §3.8).
 *
 * El adapter no sabe cuál es la facial ni cómo se llaman: eso lo resuelve
 * `detectarMallas` por sus shapes. En el avatar de desarrollo son tres
 * (cabeza con 51, pestañas con 29 y dientes inferiores con 4); un asset
 * propio puede traer otra cantidad.
 */
export type RigMeshes = readonly Mesh[];

/**
 * Huesos que el adapter rota directamente.
 *
 * Las mallas `AvatarLeftEyeball` / `AvatarRightEyeball` están skinned al
 * mismo esqueleto que el resto del cuerpo (skin 0, 73 joints): rotar
 * esos nodos de malla no mueve nada, la geometría la manda el hueso.
 * Ver `07_BLENDSHAPES_ARKIT.md` §3.3 (corregido en v1.1 a partir de
 * este hallazgo).
 */
export interface RigBones {
  /** `Head`. Lo usa la oscilación de la capa idle. */
  head: Object3D;
  /** `LeftEye`, hijo de `Head`. */
  eyeLeft: Object3D;
  /** `RightEye`, hijo de `Head`. */
  eyeRight: Object3D;
  /**
   * Hueso alto del tronco, para la respiración (tanda 4, D-3). Opcional:
   * un asset sin él respira igual de bien que una foto, pero no falla.
   */
  chest?: Object3D | null;
}

/**
 * Cuánto se atenúan, hablando a nivel máximo, los shapes de la expresión
 * que cierran o tensan la boca (D-5). 0,45 deja la mueca reconocible y
 * al mismo tiempo permite abrir la mandíbula.
 */
export const ATENUACION_BOCA = 0.45;

/**
 * Orientación de un hueso respecto de la raíz de su jerarquía (el ancestro
 * más alto), y su inversa. Se mide una vez, con el esqueleto en reposo.
 * Respecto de la raíz y no del mundo: si alguien gira la escena, la
 * mirada sigue en los ejes del avatar.
 */
function marcoEnRaiz(hueso: Object3D): { marco: Quaternion; inv: Quaternion } {
  let raiz = hueso;
  while (raiz.parent) raiz = raiz.parent;
  hueso.updateWorldMatrix(true, false);
  const enRaiz = raiz.getWorldQuaternion(new Quaternion()).invert();
  const marco = enRaiz.multiply(hueso.getWorldQuaternion(new Quaternion()));
  return { marco, inv: marco.clone().invert() };
}

/**
 * Shapes de expresión que pelean con el habla: cierran los labios, los
 * aprietan o los estiran. No incluye sonrisa ni tristeza (`mouthSmile*`,
 * `mouthFrown*`): son la emoción misma y hablar no las apaga.
 */
const SHAPES_BOCA_TENSA = [
  'mouthClose',
  'mouthPressLeft',
  'mouthPressRight',
  'mouthStretchLeft',
  'mouthStretchRight',
  'mouthRollLower',
  'mouthRollUpper',
  'mouthShrugLower',
  'mouthShrugUpper',
  'jawForward',
] as const;

const INDICES_BOCA_TENSA = Int32Array.from(SHAPES_BOCA_TENSA, (s) => IDX_ARKIT[s]);

/** Donde suma el pulso de cejas de los gestos (tanda 8). */
const INDICES_CEJAS_ARRIBA = [
  IDX_ARKIT.browInnerUp,
  IDX_ARKIT.browOuterUpLeft,
  IDX_ARKIT.browOuterUpRight,
] as const;

/** Donde suman los labios apretados de "pensando" (tanda 8). */
const INDICES_PRESION = [IDX_ARKIT.mouthPressLeft, IDX_ARKIT.mouthPressRight] as const;

/** Donde actúa el parpadeo de la idle y de los gestos. */
const INDICES_PARPADEO = [IDX_ARKIT.eyeBlinkLeft, IDX_ARKIT.eyeBlinkRight] as const;

/**
 * Amplitud de la respiración: cuánto se yergue el tronco en la
 * inspiración máxima. 0,9° — en el encuadre de busto se lee como el
 * pecho que sube, y más que eso parece un suspiro constante (P-2).
 */
const RESPIRACION_PITCH_MAX_RAD = (0.9 * Math.PI) / 180;

/** Rotación de un hueso en grados, orden XYZ. Ver `getReposoRig`. */
export interface RotacionGrados {
  x: number;
  y: number;
  z: number;
}

/** Reposo de los tres huesos que el adapter rota. */
export type ReposoRig = Record<'Head' | 'LeftEye' | 'RightEye', RotacionGrados>;

const RAD_A_GRADOS = 180 / Math.PI;

const clampSigned = (v: number): number => (v < -1 ? -1 : v > 1 ? 1 : v);

/** Opciones del adapter que no son el rig. */
export interface ThreeRigAdapterOpts {
  /** Azar de todas las capas de vida. Default `Math.random`; los tests lo fijan. */
  rng?: Rng;
}

/**
 * Cuaternión a grados XYZ. Fuera del render loop: asigna un `Euler`.
 * Redondea a un decimal — más precisión que ésa es ruido del exportador
 * y ensucia el diff entre dos `calibracion.json`.
 */
function gradosDe(q: Quaternion): RotacionGrados {
  const e = new Euler().setFromQuaternion(q, 'XYZ');
  const r = (v: number) => {
    // `+ 0` normaliza el `-0` que deja redondear un ángulo negativo
    // minúsculo: en el archivo se vería como `-0` y ensuciaría el diff
    // entre dos calibraciones del mismo avatar.
    return Math.round(v * RAD_A_GRADOS * 10) / 10 + 0;
  };
  return { x: r(e.x), y: r(e.y), z: r(e.z) };
}

/** Curva de la transición: arranque y llegada suaves, sin overshoot. */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

const RIG_KEYS = Object.keys(NEUTRAL_PARAMS) as (keyof RigParams)[];

/** Interpola **dentro** de `out`: el render loop no debe asignar. */
function lerpParamsInto(out: RigParams, from: RigParams, to: RigParams, t: number): void {
  for (const k of RIG_KEYS) out[k] = lerp(from[k], to[k], t);
}

/**
 * Adapter de `avatar-core` sobre three.js. Traduce los 11 `RigParams`
 * canónicos a pesos de blend shapes ARKit y rotación de los huesos de
 * ojo (`docs/04_ARQUITECTURA.md` §6.4).
 *
 * **La interpolación se tickea desde afuera.** `transitionTo` sólo
 * registra el destino; `update(deltaMs)` avanza el tween. El motivo es
 * que en `@react-three/fiber` ya existe un render loop sobre
 * `requestAnimationFrame`: abrir un segundo rAF desde el adapter haría
 * que los pesos se escriban desfasados respecto del frame que los
 * dibuja. El componente llama a `update()` dentro de `useFrame`.
 */
export class ThreeRigAdapter implements RigAdapter {
  private current: RigParams = { ...NEUTRAL_PARAMS };
  private from: RigParams = { ...NEUTRAL_PARAMS };
  private to: RigParams = { ...NEUTRAL_PARAMS };
  private elapsedMs = 0;
  private durationMs = 0;

  /**
   * Por malla, un array del largo del catálogo ARKit donde cada posición
   * guarda el índice de esa shape en `morphTargetInfluences`, o `-1` si
   * la malla no la declara. Se resuelve una vez al construir; escribir
   * la pose queda en un recorrido de enteros, sin `Map` ni strings.
   */
  private readonly indices: { mesh: Mesh; mapa: Int32Array }[];

  /** Buffer de la pose traducida. Se reusa entre frames. */
  private readonly pose = crearPoseBuffer();

  /**
   * Pose de la expresión sin mirada, para traducirla a pesos. La mirada de
   * todas las capas se suma aparte (`escribirMirada`). Se muta, no se
   * recrea.
   */
  private readonly sinMirada: RigParams = { ...NEUTRAL_PARAMS };

  /**
   * Rotación de reposo de cada hueso.
   *
   * En este avatar **ninguna es la identidad**: los ojos vienen con ~7°
   * de inclinación en X y la cabeza con ~-7°. Escribir `rotation.x/y`
   * directamente las borraba, y la pose quedaba sesgada incluso con
   * todo en neutro. Las rotaciones se aplican como delta sobre el
   * reposo, nunca en su lugar.
   */
  private readonly rest: {
    head: Quaternion;
    eyeLeft: Quaternion;
    eyeRight: Quaternion;
    chest: Quaternion | null;
  };

  /**
   * Orientación de cada ojo en reposo, respecto de la raíz del avatar
   * (`marco`), y su inversa. La mirada se escribe en ejes del avatar
   * —+Y arriba, +Z al frente— y se lleva a los ejes locales del hueso
   * con `marcoInv · giro · marco`.
   *
   * Hace falta porque los ejes locales del ojo no tienen por qué ser los
   * del avatar. En los de Character Creator el eje Y local apunta en la
   * dirección de la mirada: girar en Y local hacía rotar el globo sobre su
   * propio eje y la mirada horizontal no se veía (2026-10-15). La vertical
   * andaba porque el eje X local sí coincide.
   */
  private readonly marcoOjo: {
    izq: { marco: Quaternion; inv: Quaternion };
    der: { marco: Quaternion; inv: Quaternion };
  };

  /** Scratch reutilizado por frame: `writePose` corre en el render loop. */
  private readonly scratchEuler = new Euler(0, 0, 0, 'XYZ');
  private readonly scratchQuat = new Quaternion();
  private readonly scratchQuatOjo = new Quaternion();

  private readonly rng: Rng;

  /** Constantes y conmutadores de las capas de vida (tanda 8). */
  private vida: VidaOpts = { ...VIDA_DEFECTOS };
  private capas: CapasVida = { ...CAPAS_VIDA_DEFECTOS };

  /** Vida ambiental. Se compone con la expresión; nunca la reemplaza. */
  private idle: IdleLayer | null;
  private idleOffsets: IdleOffsets = IDLE_NEUTRO;

  /** Gestos acoplados al habla (tanda 8, pieza 2). */
  private readonly gestos: CapaGestos;
  private gestosOffsets: Readonly<GestosOffsets> = GESTOS_REPOSO;

  /** Modulación lenta de la expresión (tanda 8, pieza 3). */
  private readonly modulacion: CapaModulacion;
  private factorExpresion = 1;

  /** "Pensando" e inspiración del pre-roll (tanda 8, pieza 5). */
  private readonly pensando: CapaPensando;
  private readonly inspiracion: CapaInspiracion;
  private pensandoOffsets: Readonly<PensandoOffsets> = PENSANDO_REPOSO;
  private inspiracionOffsets: Readonly<InspiracionOffsets> = INSPIRACION_REPOSO;

  /** Habla por amplitud (D-4). En silencio no cuesta nada. */
  private readonly habla = new CapaHabla();
  private hablaOffsets: HablaOffsets = HABLA_SILENCIO;
  private atenuacionBoca = ATENUACION_BOCA;

  constructor(
    meshes: RigMeshes,
    private readonly bones: RigBones,
    opts: ThreeRigAdapterOpts = {},
  ) {
    this.rng = opts.rng ?? Math.random;
    this.idle = this.crearIdle();
    this.gestos = new CapaGestos({ rng: this.rng, vida: this.vida });
    this.modulacion = new CapaModulacion({ rng: this.rng, vida: this.vida });
    this.pensando = new CapaPensando({ rng: this.rng, vida: this.vida });
    this.inspiracion = new CapaInspiracion({ rng: this.rng, vida: this.vida });
    this.indices = meshes.map((mesh) => {
      const dict = mesh.morphTargetDictionary ?? {};
      const mapa = new Int32Array(ARKIT_SHAPES.length).fill(-1);
      ARKIT_SHAPES.forEach((shape, i) => {
        const j = dict[shape];
        if (j !== undefined) mapa[i] = j;
      });
      return { mesh, mapa };
    });
    this.rest = {
      head: bones.head.quaternion.clone(),
      eyeLeft: bones.eyeLeft.quaternion.clone(),
      eyeRight: bones.eyeRight.quaternion.clone(),
      chest: bones.chest ? bones.chest.quaternion.clone() : null,
    };
    this.marcoOjo = { izq: marcoEnRaiz(bones.eyeLeft), der: marcoEnRaiz(bones.eyeRight) };
    this.writePose(this.current);
  }

  /**
   * Enciende o apaga la vida ambiental. Apagarla deja la cara congelada
   * en la expresión dirigida — útil para calibrar, nunca en producto.
   */
  setIdleEnabled(enabled: boolean): void {
    if (enabled === (this.idle !== null)) return;
    this.idle = enabled ? this.crearIdle() : null;
    this.idleOffsets = IDLE_NEUTRO;
    this.writePose(this.current);
  }

  private crearIdle(): IdleLayer {
    return new IdleLayer({ rng: this.rng, vida: this.vida, miradaActiva: this.capas.miradaIdle });
  }

  /**
   * Enciende o apaga las capas de vida de la tanda 8. Apagar una la deja en
   * reposo de inmediato, sin decaer: el recorrido de capturas lo necesita
   * para que dos capturas de la misma pose coincidan.
   */
  setCapasVida(capas: Partial<CapasVida>): void {
    this.capas = { ...this.capas, ...capas };
    this.idle?.setMiradaActiva(this.capas.miradaIdle);
    if (!this.capas.gestosHabla) {
      this.gestos.reiniciar();
      this.gestosOffsets = GESTOS_REPOSO;
    }
    if (!this.capas.modulacion) {
      this.modulacion.reiniciar();
      this.factorExpresion = 1;
    }
    if (!this.capas.pensando) {
      this.pensando.reiniciar();
      this.pensandoOffsets = PENSANDO_REPOSO;
    }
    if (!this.capas.preroll) {
      this.inspiracion.reiniciar();
      this.inspiracionOffsets = INSPIRACION_REPOSO;
    }
    this.writePose(this.current);
  }

  getCapasVida(): CapasVida {
    return { ...this.capas };
  }

  /** Calibración en vivo desde el panel "Vida" de `/dev/avatar`. */
  configurarVida(vida: Partial<VidaOpts>): void {
    this.vida = { ...this.vida, ...vida };
    this.idle?.configurar(this.vida);
    this.gestos.configurar(this.vida);
    this.modulacion.configurar(this.vida);
    this.pensando.configurar(this.vida);
    this.inspiracion.configurar(this.vida);
  }

  /**
   * El turno entró o salió de proceso (tanda 8, pieza 5). Lo decide el
   * orquestador por la fase del turno; el adapter solo lo dibuja, y nada si
   * la capa está apagada.
   */
  pensar(activo: boolean): void {
    if (activo && this.capas.pensando) this.pensando.activar();
    else this.pensando.desactivar();
  }

  /**
   * Empieza un pre-roll de `prerollMs`: en su final, una inspiración. Se
   * suelta sola al arrancar la voz (`hablar`).
   */
  inspirar(prerollMs: number): void {
    if (this.capas.preroll) this.inspiracion.iniciar(prerollMs);
  }

  transitionTo(params: RigParams, durationMs: number): void {
    // El punto de partida es la pose de este instante, no el destino
    // anterior: interrumpir una transición a mitad no debe dar un salto.
    this.from = { ...this.current };
    this.to = { ...params };
    this.elapsedMs = 0;
    this.durationMs = Math.max(0, durationMs);
    if (this.durationMs === 0) {
      this.current = { ...params };
      this.writePose(this.current);
    }
  }

  getCurrentParams(): RigParams {
    return { ...this.current };
  }

  /**
   * Rotación de reposo de los huesos que el adapter rota, en grados.
   *
   * La escribe la página de calibración en `calibracion.json`: es el dato
   * que explica por qué dos avatares con la misma pose "se ven distinto".
   * En el avatar de desarrollo ninguna es la identidad (ojos ~7° en X,
   * cabeza ~-7°), y el adapter rota como delta sobre ella; un asset con
   * otro reposo desplaza todas las expresiones por igual.
   */
  getReposoRig(): ReposoRig {
    return {
      Head: gradosDe(this.rest.head),
      LeftEye: gradosDe(this.rest.eyeLeft),
      RightEye: gradosDe(this.rest.eyeRight),
    };
  }

  /**
   * Avanza el tween y la capa idle. Lo llama el render loop de R3F vía
   * `useFrame`.
   */
  update(deltaMs: number): void {
    const enTransicion = this.durationMs > 0;
    if (enTransicion) {
      this.elapsedMs += deltaMs;
      const t = Math.min(1, this.elapsedMs / this.durationMs);
      lerpParamsInto(this.current, this.from, this.to, smoothstep(t));
      if (t >= 1) this.durationMs = 0;
    }

    const gestosActivos = this.capas.gestosHabla;
    if (this.idle) {
      // Mientras el paciente habla, la idle parpadea menos: los parpadeos
      // pasan a caer en las pausas, que los pone la capa de gestos.
      // Pensando, parpadea más lento.
      this.idle.setTasaParpadeo(
        gestosActivos && this.habla.hablando
          ? this.vida.parpadeoTasaHablando
          : this.pensando.pensando
            ? this.vida.pensandoTasaParpadeo
            : 1,
      );
      this.idleOffsets = this.idle.update(deltaMs);
    }

    // La capa de habla se tickea siempre que haya algo que decaer: al
    // cortar el audio el nivel baja con su constante de caída, no de
    // golpe.
    const habiaHabla = this.hablaOffsets.nivel > 0;
    this.hablaOffsets = this.habla.update(deltaMs);

    const habiaGestos = this.gestos.activo;
    if (gestosActivos) {
      this.gestosOffsets = this.gestos.update(
        deltaMs,
        this.hablaOffsets.nivel,
        this.habla.hablando,
        this.habla.umbralPausa,
      );
    }

    if (this.capas.modulacion) {
      this.factorExpresion = this.modulacion.update(
        deltaMs,
        this.hablaOffsets.nivel,
        this.habla.hablando,
        this.habla.umbralPausa,
      );
    }

    const habiaTurno = this.pensando.activo || this.inspiracion.activo;
    this.pensandoOffsets = this.pensando.update(deltaMs);
    this.inspiracionOffsets = this.inspiracion.update(deltaMs);

    // Sin transición, ni idle, ni habla, ni gestos, ni modulación, ni turno
    // no hay nada nuevo que escribir: el rig ya tiene la pose. Evita
    // recorrer 51 influencias por frame en reposo.
    if (
      habiaTurno ||
      enTransicion ||
      this.capas.modulacion ||
      this.idle ||
      habiaHabla ||
      this.hablaOffsets.nivel > 0 ||
      habiaGestos ||
      this.gestos.activo
    ) {
      this.writePose(this.current);
    }
  }

  /**
   * Empieza a mover la boca con la envolvente de un audio (D-4).
   *
   * @param desfaseMs cuánto lleva sonando ese audio: la envolvente se
   *   calcula en paralelo y puede llegar tarde.
   */
  hablar(envolvente: EnvolventeAudio, desfaseMs = 0): void {
    this.inspiracion.soltar();
    this.habla.iniciar(envolvente, desfaseMs);
  }

  /** Corta el habla. La boca cierra con la constante de caída. */
  dejarDeHablar(): void {
    this.habla.detener();
  }

  /** Calibración en vivo desde `/dev/avatar`. El producto usa los defaults. */
  configurarHabla({ atenuacionBoca, ...opts }: HablaOpts & { atenuacionBoca?: number }): void {
    if (atenuacionBoca !== undefined) this.atenuacionBoca = atenuacionBoca;
    this.habla.configurar(opts);
  }

  /**
   * Suma el habla sobre los pesos ya traducidos de la expresión (D-5).
   *
   * Mientras hay voz, los shapes que **cierran o tensan la boca** se
   * atenúan: `DOLOR` sigue siendo dolor en cejas y ojos, pero la
   * mandíbula tiene que poder abrirse para hablar. La atenuación es
   * proporcional al nivel, así que al callarse la expresión vuelve
   * entera sin salto.
   *
   * El parpadeo y la mirada no se tocan: hablar no cambia los ojos.
   */
  private componerHabla(pesos: Float32Array): void {
    const h = this.hablaOffsets;
    if (h.nivel === 0) return;

    const factor = 1 - (1 - this.atenuacionBoca) * h.nivel;
    for (let i = 0; i < INDICES_BOCA_TENSA.length; i++) {
      const j = INDICES_BOCA_TENSA[i] ?? -1;
      if (j >= 0) pesos[j] = (pesos[j] ?? 0) * factor;
    }

    const sumar = (indice: number, valor: number) => {
      pesos[indice] = Math.min(1, (pesos[indice] ?? 0) + valor);
    };
    sumar(IDX_ARKIT.jawOpen, h.mandibula);
    sumar(IDX_ARKIT.mouthFunnel, h.embudo);
    sumar(IDX_ARKIT.mouthPucker, h.fruncido);
    sumar(IDX_ARKIT.mouthClose, h.cierre);
  }

  /**
   * Suma los gestos del habla que no son de boca (tanda 8): el pulso de
   * cejas. Se suma sobre lo que tenga la expresión y se recorta a 1; nunca
   * la reemplaza.
   */
  private componerGestos(pesos: Float32Array): void {
    const cejas = this.gestosOffsets.cejas;
    if (cejas > 0) {
      for (const i of INDICES_CEJAS_ARRIBA) pesos[i] = Math.min(1, (pesos[i] ?? 0) + cejas);
    }
    // Pensando: labios apenas apretados. Inspiración: la boca se entreabre.
    const presion = this.pensandoOffsets.presion;
    if (presion > 0) {
      for (const i of INDICES_PRESION) pesos[i] = Math.min(1, (pesos[i] ?? 0) + presion);
    }
    const jaw = this.inspiracionOffsets.mandibula;
    if (jaw > 0) pesos[IDX_ARKIT.jawOpen] = Math.min(1, (pesos[IDX_ARKIT.jawOpen] ?? 0) + jaw);
  }

  /**
   * Compone las capas, traduce a ARKit y escribe en el rig.
   *
   * La composición ocurre **acá y no en el motor**: `avatar-core` sigue
   * empujando el rig a un target y no sabe que las capas existen
   * (`04_ARQUITECTURA.md` §6.6). El orden importa:
   *
   * 1. La expresión se traduce sola, sin mirada: son sus pesos, con su
   *    `intensidad` ya aplicada. Se multiplican por el factor de la
   *    modulación lenta (pieza 3), que solo toca estos pesos.
   * 2. El habla atenúa la boca tensa y suma la suya; los gestos suman las
   *    cejas. Todo se recorta a `0..1`.
   * 3. El parpadeo, relativo a la apertura que quedó: en su pico cierra el
   *    ojo del todo aunque la expresión lo entrecierre, y nunca lo abre.
   * 4. La mirada de todas las capas se suma en unidades de `mirada_*` y
   *    se traduce una sola vez, recortada a los límites del rig.
   *
   * Ni la idle ni los gestos pasan por `intensidad`: parpadear o asentir
   * no es expresar (`08_CALIBRACION.md` §3).
   */
  /**
   * `reposo · marcoInv · giro · marco`: el giro de `scratchQuat`, que está
   * en ejes del avatar, aplicado en los ejes locales del hueso.
   */
  private girarOjo(
    hueso: Object3D,
    reposo: Quaternion,
    { marco, inv }: { marco: Quaternion; inv: Quaternion },
  ): void {
    this.scratchQuatOjo.copy(inv).multiply(this.scratchQuat).multiply(marco);
    hueso.quaternion.copy(reposo).multiply(this.scratchQuatOjo);
  }

  private writePose(params: RigParams): void {
    const idle = this.idleOffsets;
    const gestos = this.gestosOffsets;
    const pensando = this.pensandoOffsets;

    const base = this.sinMirada;
    for (const k of RIG_KEYS) base[k] = params[k];
    base.mirada_x = 0;
    base.mirada_y = 0;
    escribirArkit(base, this.pose);
    const pesos = this.pose.pesos;
    const gaze = this.pose.gaze;

    // Antes que todo lo demás: el factor es de la expresión y no debe
    // alcanzar al habla, los gestos, el parpadeo ni la mirada. Que la
    // atenuación de la boca venga después da igual, porque las dos
    // multiplican.
    const f = this.factorExpresion;
    if (f !== 1) for (let i = 0; i < pesos.length; i++) pesos[i] = Math.min(1, (pesos[i] ?? 0) * f);

    this.componerHabla(pesos);
    this.componerGestos(pesos);

    const parpadeo = Math.max(idle.parpadeo, gestos.parpadeo);
    if (parpadeo > 0) {
      for (const i of INDICES_PARPADEO) {
        const e = pesos[i] ?? 0;
        pesos[i] = e + (1 - e) * parpadeo;
      }
    }

    const k = clamp(params.intensidad, 0, 1);
    escribirMirada(
      clampSigned(params.mirada_x) * k + idle.miradaX + gestos.miradaX + pensando.miradaX,
      clampSigned(params.mirada_y) * k + idle.miradaY + gestos.miradaY + pensando.miradaY,
      this.pose,
    );

    for (const { mesh, mapa } of this.indices) {
      const influences = mesh.morphTargetInfluences;
      if (!influences) continue;
      // Reset: un shape que dejó de estar en la pose debe volver a 0.
      influences.fill(0);
      for (let i = 0; i < mapa.length; i++) {
        const j = mapa[i] ?? -1;
        if (j >= 0) influences[j] = pesos[i] ?? 0;
      }
    }

    // `GazeRotation` es semántica: yaw positivo = el avatar mira a su
    // derecha, pitch positivo = mira arriba. Traducirla a three.js
    // invierte los dos signos, y conviene dejar escrito por qué.
    //
    // El avatar mira a +Z con +Y arriba (convención glTF). El giro se
    // arma en esos ejes, no en los locales del hueso (ver `marcoOjo`).
    // En un sistema diestro, con el frente en +Z: la derecha del avatar
    // es -X. Una rotación +Y lleva el frente hacia +X (su izquierda) y
    // una +X lo lleva hacia -Y (abajo). O sea que ambos ejes van al revés
    // de la semántica, y de ahí los dos signos negativos.
    this.scratchEuler.set(-gaze.pitch, -gaze.yaw, 0, 'XYZ');
    this.scratchQuat.setFromEuler(this.scratchEuler);
    this.girarOjo(this.bones.eyeLeft, this.rest.eyeLeft, this.marcoOjo.izq);
    this.girarOjo(this.bones.eyeRight, this.rest.eyeRight, this.marcoOjo.der);

    // La cabeza la mueven la idle y los gestos: ningún `RigParam` la
    // controla. Misma convención de signos que los ojos. La suma se
    // recorta por eje, para que ninguna combinación de capas se lea como
    // un tic.
    const cabezaMax = this.vida.cabezaMaxGrados * GRADOS_A_RAD;
    this.scratchEuler.set(
      -clamp(
        idle.cabezaPitch + gestos.cabezaPitch + this.inspiracionOffsets.cabezaPitch,
        -cabezaMax,
        cabezaMax,
      ),
      -clamp(idle.cabezaYaw + gestos.cabezaYaw, -cabezaMax, cabezaMax),
      clamp(gestos.cabezaRoll, -cabezaMax, cabezaMax),
      'XYZ',
    );
    this.scratchQuat.setFromEuler(this.scratchEuler);
    this.bones.head.quaternion.copy(this.rest.head).multiply(this.scratchQuat);

    // Respiración (D-3): el tronco se yergue apenas al inspirar. Es el
    // gesto más barato que existe y el que más descarta "es una foto".
    // Sin hueso de tronco, no pasa nada.
    if (this.bones.chest && this.rest.chest) {
      this.scratchEuler.set(-idle.respiracion * RESPIRACION_PITCH_MAX_RAD, 0, 0, 'XYZ');
      this.scratchQuat.setFromEuler(this.scratchEuler);
      this.bones.chest.quaternion.copy(this.rest.chest).multiply(this.scratchQuat);
    }
  }
}
